import { ActionIcon, Box, Flex, NumberInput } from '@mantine/core';
import { useCounter } from '@mantine/hooks';
import { IconMinus, IconPlus, IconTrash } from '@tabler/icons';
import { FC, useEffect } from 'react';

type Props = {
  initialCount?: number;
  min?: number;
  onChanged?: (count: number) => void;
  deleteDisabled?: boolean;
  onLastRemoved?: () => void;
};

const IncDecCounter: FC<Props> = ({ initialCount = 0, min = 0, deleteDisabled, onChanged, onLastRemoved }) => {
  const [count, { increment, decrement, set }] = useCounter(initialCount ?? 0, { min });
  const controlHeight = 30;

  useEffect(() => {
    if (count !== initialCount) {
      onChanged?.(count);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  const showingTrashIcon = count <= min + 1;

  return (
    <Box
      className="inc-dec-counter"
      sx={(theme) => ({
        width: 96,
        maxWidth: 150,
        padding: 0,
        borderRadius: theme.radius.xl,
        overflow: 'hidden',
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : '#fff',
        boxShadow: theme.colorScheme === 'dark' ? 'none' : '0 1px 2px rgba(15, 23, 42, 0.08)',
      })}
    >
      <Flex align="stretch" justify="center" sx={{ height: controlHeight }}>
        <ActionIcon
          size={controlHeight}
          disabled={showingTrashIcon && deleteDisabled}
          variant="transparent"
          color={showingTrashIcon ? 'red' : 'gray'}
          radius={0}
          sx={(theme) => ({
            height: controlHeight,
            minHeight: controlHeight,
            minWidth: controlHeight,
            borderRight: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
            borderRadius: 0,
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
            color: showingTrashIcon
              ? theme.colorScheme === 'dark'
                ? theme.colors.red[3]
                : theme.colors.red[6]
              : theme.colorScheme === 'dark'
              ? theme.colors.gray[2]
              : theme.colors.gray[7],
            '&:hover': {
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
            },
          })}
          onClick={showingTrashIcon ? onLastRemoved : decrement}
        >
          {!showingTrashIcon && <IconMinus size={13} />}
          {showingTrashIcon && <IconTrash size={13} />}
        </ActionIcon>
        <NumberInput
          hideControls
          value={count}
          onChange={set}
          sx={(theme) => ({
            flex: 1,
            minWidth: 0,
            justifyContent: 'center',
            input: {
              height: controlHeight,
              minHeight: controlHeight,
              textAlign: 'center',
              border: 'none',
              borderRadius: 0,
              padding: '0 8px',
              fontSize: 12,
              fontWeight: 600,
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
              color: theme.colorScheme === 'dark' ? theme.white : theme.black,
              '&:focus': {
                border: 'none',
              },
            },
          })}
        />
        <ActionIcon
          size={controlHeight}
          variant="transparent"
          onClick={increment}
          radius={0}
          sx={(theme) => ({
            height: controlHeight,
            minHeight: controlHeight,
            minWidth: controlHeight,
            borderLeft: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
            borderRadius: 0,
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[6] : theme.white,
            color: theme.colorScheme === 'dark' ? theme.colors.gray[2] : theme.colors.gray[7],
            '&:hover': {
              backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
            },
          })}
        >
          <IconPlus size={13} />
        </ActionIcon>
      </Flex>
    </Box>
  );
};

export default IncDecCounter;
